import React, { useState, useEffect } from "react";
import { FaRegHeart } from "react-icons/fa";
import { FaCartPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "../other";
import { addToCartThunk } from "../../redux/actions/cartActions";
import {
  toggleWishlistItem,
  fetchWishlist,
} from "../../redux/actions/wishlistAction";
import { getTrendingProducts } from "../../redux/actions/popularProductsAction";

import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Autoplay,
} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";

function LandingPopularProducts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const { loading, pop_products, error } = useSelector(
    (state) => state.popularProducts
  );

  const [activeFilter, setActiveFilter] = useState("All");
  const [filterItems, setFilterItems] = useState(["All"]);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [subProducts, setSubProducts] = useState([]);
  const [quantities, setQuantities] = useState({});

  // Chunk array helper (to split products into groups of 2 for vertical pairs)
  const chunkArray = (arr, size) => {
    const result = [];

    for (let i = 0; i < arr.length; i += size) {
      if (arr[i].subProducts.length > 0) {
        result.push(arr.slice(i, i + size));
      }
    }
    return result;
  };

  // 1️⃣ fetch trending & wishlist
  useEffect(() => {
    dispatch(getTrendingProducts());
    dispatch(fetchWishlist());
  }, [dispatch]);

  // 2️⃣ build filters off cat_name
  useEffect(() => {
    if (pop_products?.length) {
      const updatedList = new Set(["All"]);
      pop_products.forEach((p) => {
        if (p.cat_name) updatedList.add(p.cat_name);
      });
      setFilterItems([...updatedList]);
    }
  }, [pop_products]);

  // 3️⃣ map pop_products → local products shape
  useEffect(() => {
    if (!pop_products) return;
    const list = pop_products.map((product) => ({
      id: product.pro_id,
      title: product.product_name,
      type: product.product_sub_title || "N/A",
      category: product.cat_name,
      image: product.product_primary_img,
      subProducts: (product.SubProducts || []).map((sub) => ({
        sub_product_id: sub.sub_pro_id,
        size: sub.size,
        color: sub.color_id,
        subImage: sub.sub_pro_img,
        no_of_pcs: sub.no_of_pcs,
        stock: sub.no_of_stock,
        type: sub.selling_type,
        total_ordered_quantity: sub.total_ordered_quantity,
        price: sub.customer_product_price,
        vendorPrice: sub.vendor_product_price,
      })),
    }));
    setProducts(list);
  }, [pop_products]);

  // Handlers
  const handleAddToCartClick = (e, product) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
    }
    if (product?.subProducts?.length) {
      setCurrentProduct(product);
      setSubProducts(product.subProducts);
      const initialQuantities = {};
      product.subProducts.forEach(
        (sub) => (initialQuantities[sub.sub_product_id] = 1)
      );
      setQuantities(initialQuantities);
      setIsModalOpen(true);
    } else {
      alert("No sub-products available for this item.");
    }
  };
  const incrementQuantity = (sub) => {
    const temp = { ...quantities };
    let qty =
      userType === "vendor"
        ? (temp[sub.sub_product_id] + 1) * Number(sub.no_of_pcs)
        : temp[sub.sub_product_id] + 1;
    if (qty > Number(sub.stock)) {
      alert("Insufficient stock");
    } else {
      setQuantities((prev) => ({
        ...prev,
        [sub.sub_product_id]: (prev[sub.sub_product_id] || 1) + 1,
      }));
    }
  };
  const decrementQuantity = (id) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: prev[id] > 1 ? prev[id] - 1 : 1,
    }));
  };
  const handleAddSubProductToCart = (sub) => {
    const product_id = currentProduct.id;
    const sub_product_id = sub.sub_product_id;
    const quantity = quantities[sub_product_id] || 1;
    if (!product_id || !sub_product_id) return;
    dispatch(addToCartThunk({ product_id, sub_product_id, quantity }));
  };

  const handleQuickViewClick = (product) => {
    setCurrentProduct(product);
    navigate(`/ProductsDetails/${product.id}`);
  };
  const handleWishlist = (e, productId) => {
    e.stopPropagation();
    if (!productId) return;
    dispatch(toggleWishlistItem(productId));
  };
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const filteredProducts =
    activeFilter === "All"
      ? products
      : products.filter((p) => p.category === activeFilter);

  // chunk products into pairs of 2 for vertical stacking per slide
  const chunkedProducts = chunkArray(filteredProducts, 2);

  if (error) return <p className="text-center text-danger mt-5">{error}</p>;

  return (
    <section>
      <div className="container flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row justify-between items-center">
          {/* Title Section */}
          <h2 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl fw-bold">
            Most Popular Products
          </h2>

          {/* Filter Section */}
          <div className="border border-dark rounded-5 px-3 w-full md:w-1/2">
            <ul className="list-unstyled flex justify-content-between py-2 gap-1 align-items-center">
              {filterItems.map((filter, idx) => (
                <li
                  key={idx}
                  className={`filter-item text-center font-weight-medium px-3 rounded-xl hover:bg-theme-color cursor-pointer ${
                    activeFilter === filter && "bg-theme-color"
                  }`}
                  onClick={() => handleFilterClick(filter)}
                >
                  {filter}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {loading ? (
          <p className="text-center mt-5">Loading...</p>
        ) : (
          <>
            {filteredProducts.length === 0 ? (
              <p className="text-center">
                No products found for "{activeFilter}"
              </p>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
                spaceBetween={24}
                slidesPerView={4}
                // navigation
                // pagination={{ clickable: true }}
                scrollbar={{ draggable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                // loop={true}
                breakpoints={{
                  0: { slidesPerView: 2, spaceBetween: 10 },
                  640: { slidesPerView: 3, spaceBetween: 15 },
                  768: { slidesPerView: 4, spaceBetween: 20 },
                }}
                className="pb-5 w-full"
              >
                {chunkedProducts.map((pair, index) => (
                  <SwiperSlide key={index}>
                    <div className="flex flex-col gap-6">
                      {pair.map((product) => {
                        const isWishlisted = wishlistItems.some(
                          (item) =>
                            (item.product?.pro_id || item.pro_id) === product.id
                        );

                        return product.subProducts.length > 0 ? (
                          <div
                            key={product.id}
                            className="cursor-pointer select-none"
                            onClick={() => handleQuickViewClick(product)}
                          >
                            <div
                              onClick={() => handleQuickViewClick(product)}
                              key={product.id}
                              className="cursor-pointer relative"
                            >
                              <div className="aspect-[3/4] overflow-hidden rounded-3xl">
                                <img
                                  loading="lazy"
                                  src={product.image}
                                  alt={product.title}
                                  className="object-fit size-full"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src =
                                      "https://via.placeholder.com/150?text=No+Image";
                                  }}
                                />
                              </div>
                              <div className="d-flex align-items-center justify-content-between gap-2 mt-1 flex-wrap">
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="text-truncate pb-0 fs-5 fs-6">
                                    <b>{product.title}</b>
                                  </span>
                                  <small className="text-truncate pt-0 fw-bold -mb-2">
                                    {product.type}
                                  </small>
                                </div>
                                {isAuthenticated && (
                                  <div className="d-flex align-items-center justify-content-end">
                                    <div className="px-2 py-1 text-base font-semibold rounded-xl bg-white">
                                      {userType === "vendor"
                                        ? product.subProducts[0]?.vendorPrice
                                        : product.subProducts[0]?.price}
                                      <span> ₹</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="position-absolute top-2 end-0 mr-2 d-flex flex-column group-hover:-translate-y-5 lg:pr-7">
                                <button
                                  onClick={(e) => handleWishlist(e, product.id)}
                                  className={`badge rounded-circle p-2 fs-6 mt-2 transition-colors ${
                                    isWishlisted
                                      ? "bg-red-500 text-white"
                                      : "bg-tealish text-white"
                                  }`}
                                  title={
                                    isWishlisted
                                      ? "Remove from Wishlist"
                                      : "Add to Wishlist"
                                  }
                                >
                                  <FaRegHeart />
                                </button>
                                <button
                                  onClick={(e) =>
                                    handleAddToCartClick(e, product)
                                  }
                                  className="badge rounded-circle bg-tealish text-white p-2 fs-6 mt-2"
                                >
                                  <FaCartPlus />
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}{" "}
          </>
        )}
      </div>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Select Quantity"
        >
          <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto px-4 py-2">
            {subProducts.map((sub) => (
              <div
                key={sub.sub_product_id}
                className="flex justify-between items-center border rounded-xl p-3"
              >
                <div className="flex items-center gap-3">
                  <img
                    loading="lazy"
                    src={
                      sub.subImage ||
                      sub.image ||
                      "https://via.placeholder.com/60x80?text=Img"
                    }
                    alt={sub.size}
                    className="w-[60px] h-[80px] object-cover rounded-lg"
                  />
                  <div>
                    <p className="font-semibold">{sub.size}</p>
                    <p className="text-sm text-gray-500">{sub.color}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => decrementQuantity(sub.sub_product_id)}
                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                  >
                    -
                  </button>
                  <span className="w-6 text-center">
                    {quantities[sub.sub_product_id] || 1}
                  </span>
                  <button
                    onClick={() => incrementQuantity(sub)}
                    className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={() => handleAddSubProductToCart(sub)}
                  className="ml-4 bg-tealish text-white px-4 py-2 rounded-lg"
                >
                  Add to Cart
                </button>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </section>
  );
}

export default LandingPopularProducts;
